import { useState } from "react";
import {
  describeAuthError,
  registerWithEmail,
  signInWithEmail,
  signInWithGoogle,
} from "../lib/firebase";
import { Button, ErrorNote, Input, cx } from "./ui";

/**
 * A compact sign-in for the one-shot detours — a claim link, an invite link.
 *
 * Deliberately narrower than AuthPage's: someone arrives here holding a link, with one thing
 * they came to do, and the screen exists to get them past the account step and back to it.
 * Shared between those flows so "sign in" behaves identically wherever it interrupts, rather
 * than drifting into two subtly different forms.
 */
export function InlineSignIn({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setError(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="label mb-2 text-accent-text">{eyebrow}</p>
        <h1 className="display-title text-3xl text-fg">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-fg-subtle">{subtitle}</p>
      </div>

      <Button variant="secondary" size="lg" block loading={busy} onClick={() => void run(signInWithGoogle)}>
        Continue with Google
      </Button>

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-line" />
        <span className="text-[12px] font-medium uppercase tracking-wider text-fg-faint">
          or with email
        </span>
        <span className="h-px flex-1 bg-line" />
      </div>

      <div className="grid grid-cols-2 rounded-2xl border border-line bg-surface-sunken p-1">
        {(["login", "register"] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={mode === value}
            onClick={() => {
              setMode(value);
              setError(null);
            }}
            className={cx(
              "min-h-10 rounded-xl px-3 py-2 text-sm font-semibold transition-all duration-200",
              mode === value ? "bg-accent text-accent-fg shadow-sm" : "text-fg-subtle hover:text-fg",
            )}
          >
            {value === "login" ? "Sign in" : "Create account"}
          </button>
        ))}
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void run(() =>
            mode === "login"
              ? signInWithEmail(email, password)
              : registerWithEmail(email, password, ""),
          );
        }}
        className="space-y-4"
      >
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
          autoComplete="email"
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
          autoComplete={mode === "login" ? "current-password" : "new-password"}
        />
        {error && <ErrorNote>{error}</ErrorNote>}
        <Button type="submit" size="lg" loading={busy} block>
          {mode === "login" ? "Sign in" : "Create account"}
        </Button>
      </form>
    </div>
  );
}
